import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import 'react-datepicker/dist/react-datepicker.css';

import { useViewport } from '../../contexts/ViewportContext';
import { useProjects } from '../../hooks/queries/useProjects';
import { useCalendarPreferences } from '../../hooks/useCalendarPreferences';
import { useInitialLoadingScreenManager } from '../../hooks/useInitialLoadingScreenManager';
import { logger } from '../../utils/logger';

// Use the enhanced hooks
import { useDashboardData } from './hooks/useDashboardData';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import { TaskItem } from './types/dashboardTypes';

// Import drag and drop related components and hooks
import { useDashboardDnd } from './hooks/useDashboardDnd';
import DashboardDesktopView from './DashboardDesktopView.tsx';

/**
 * Dashboard with drag and drop functionality
 */
const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  // Use React Query to get projects instead of projectStore
  const { data: projects = [] } = useProjects();
  const { isMobile } = useViewport();
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Using the hook will ensure calendar locales are initialized
  useCalendarPreferences();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // Load dashboard data
  const {
    isLoading: storeIsLoading,
    editingTask,
    setEditingTask,
    handleUpdateTask,
    handleDeleteTask,
    overdueTasks,
    todayTasks,
    thisWeekTasks,
    highlightedTasks,
    initUserFilter,
    // Filter-related data and functions
    activeFilters,
    setUserFilter,
    setProjectFilter,
    setTagFilter,
    resetFilters,
    saveDashboardFilters: saveDashboardFiltersFunc,
    loadDashboardFilters: loadDashboardFiltersFunc,
    users,
    projects: dashboardProjects,
    availableTags,
  } = useDashboardData();

  // Use the enhanced filter management hook - now with props
  const {
    projectFilters,
    userFilters,
    selectedTags,
    toggleProjectFilter,
    toggleUserFilter,
    setSelectedTags,
    loadDashboardFilters,
    activeUserIds,
  } = useDashboardFilters({
    activeFilters,
    setUserFilter,
    setProjectFilter,
    setTagFilter,
    resetFilters,
    saveDashboardFilters: saveDashboardFiltersFunc,
    loadDashboardFilters: loadDashboardFiltersFunc,
    users,
    projects: dashboardProjects,
    availableTags,
  });

  // Mark dashboard as fully loaded when data is ready
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    // Only mark as complete when loading is done and user filters are initialized
    if (!storeIsLoading && initUserFilter !== null && !hasMarkedCompleteRef.current) {
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [storeIsLoading, initUserFilter]); // Remove markRouteComplete from deps to prevent infinite updates

  // Load saved filters when component mounts
  useEffect(() => {
    loadDashboardFilters();
    logger.component('Loaded saved dashboard filters');
  }, [loadDashboardFilters]);

  // Realtime updates are now handled by the global RealtimeQueryProvider
  // No need for component-specific subscription hooks

  // Check if tasks arrays exist before using them
  const rawTasks = [
    ...(overdueTasks || []),
    ...(todayTasks || []),
    ...(thisWeekTasks || []),
    ...(highlightedTasks || []),
  ];

  // Use the React Query based drag and drop hook - all hooks must be called unconditionally
  const {
    activeTaskId,
    activeTask,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    showDatePicker,
    setShowDatePicker,
    taskForDatePicker,
    handleDateSelection,
    resetDragState,
  } = useDashboardDnd(rawTasks);

  // Navigate to task in its topic
  const navigateToTask = (task: TaskItem) => {
    // Handle both snake_case and camelCase versions based on data source
    const topicInfo = task.topics as { project_id?: string; projectId?: string; id: string };
    // Get the appropriate property based on what's available
    const projectId = topicInfo?.projectId || topicInfo?.project_id;
    if (!topicInfo || !projectId) return;
    navigate(`/project/${projectId}/topic/${topicInfo.id}`);
  };

  // Keep dashboard in loading state until filters are initialized
  const isLoading = storeIsLoading || initUserFilter === null;

  // Render the stateless component with all props
  return (
    <DashboardDesktopView
      // Viewport and UI state
      isMobile={isMobile}
      isDrawerOpen={isDrawerOpen}
      setIsDrawerOpen={setIsDrawerOpen}
      isFilterDropdownOpen={isFilterDropdownOpen}
      setIsFilterDropdownOpen={setIsFilterDropdownOpen}
      // Task data
      isLoading={isLoading}
      overdueTasks={overdueTasks}
      todayTasks={todayTasks}
      thisWeekTasks={thisWeekTasks}
      highlightedTasks={highlightedTasks}
      // Project and user filters
      projects={projects}
      projectFilters={projectFilters}
      toggleProjectFilter={toggleProjectFilter}
      userFilters={userFilters}
      toggleUserFilter={toggleUserFilter}
      activeUserIds={activeUserIds}
      // Tag filters
      availableTags={availableTags}
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      // Task actions
      editingTask={editingTask}
      setEditingTask={setEditingTask}
      handleUpdateTask={handleUpdateTask}
      handleDeleteTask={handleDeleteTask}
      navigateToTask={navigateToTask}
      // Drag and drop state
      activeTaskId={activeTaskId}
      activeTask={activeTask}
      showDatePicker={showDatePicker}
      setShowDatePicker={setShowDatePicker}
      taskForDatePicker={taskForDatePicker}
      // Drag and drop handlers
      handleDragStart={handleDragStart}
      handleDragOver={handleDragOver}
      handleDragEnd={handleDragEnd}
      handleDateSelection={handleDateSelection}
      resetDragState={resetDragState}
    />
  );
};

export default Dashboard;
